import { ErrorScreen } from '@/clients/shared-components/ErrorScreen';
import { FacebookPixel } from '@/clients/shared-components/FacebookPixel';
import { GooglePixel } from '@/clients/shared-components/GooglePixel';
import {
  AuthedUserContextProvider,
  useAuthedUserContext,
} from '@/clients/shared-hooks/use-authed-user';
import { useRudderstack } from '@/clients/shared-hooks/use-rudderstack';
import { UserType } from '@/ifaces/user';
import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { BasicLoading } from './components/BasicLoading';
import { PromoContextProvider } from './hooks/use-promo-context';

export function AuthedContext() {
  return (
    <AuthedUserContextProvider>
      <PromoContextProvider>
        <AuthedLoader />
      </PromoContextProvider>
    </AuthedUserContextProvider>
  );
}

function AuthedLoader() {
  const auth = useAuthedUserContext();
  const analytics = useRudderstack();
  useEffect(() => {
    if (auth.ready && auth.user_id && analytics) {
      const authTracked = localStorage.getItem('auth-tracked');
      if (!authTracked) {
        analytics?.identify(
          auth?.user_id,
          {
            name: auth.profile?.full_name || '',
            email: auth.email,
          },
          {},
          () => {},
        );
        analytics?.track(
          'Signed In',
          {
            app: 'ProfAI',
            email: auth.email,
          },
          {
            userId: auth.user_id,
          },
        );
        localStorage.setItem('auth-tracked', 'set');
      }
    }

    const handleBeforeUnload = () => {
      localStorage.removeItem('auth-tracked');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [analytics, auth]);

  if (!auth.ready) {
    return <BasicLoading />;
  }

  return auth.error ? (
    // eslint-disable-next-line lingui/no-unlocalized-strings
    <ErrorScreen message="Authentication failed" />
  ) : (
    <>
      <Outlet />
      {auth.user_type === UserType.Consumer && (
        <>
          <FacebookPixel authedUser={true} />
          <GooglePixel authedUser={true} />
        </>
      )}
    </>
  );
}
